/*
 * DataTable: lists with search, filters, sorting, pagination and row actions. A table from `md` up; stacked
 * cards on phones. Loading, empty, no-results and error states are built in.
 *
 * Client mode (all rows in memory: filters, sorting and paging happen here):
 *   <DataTable
 *     caption={t('servers:title')}
 *     rows={q.data?.items} loading={q.isPending} error={q.error} onRetry={q.refetch}
 *     getRowId={(s) => s.id}
 *     rowHref={(s) => `/servers/${s.id}`}
 *     search={{ placeholder: t('servers:search') }}
 *     filters={[{ id: 'licenseState', label: t('…'), options: LICENSE_STATES.map((v) => ({ value: v, label: t(`states.license.${v}`) })) }]}
 *     columns={[
 *       { id: 'name', header: t('…'), cell: (s) => s.name, value: (s) => s.name, sortable: true, mobile: 'title' },
 *       { id: 'state', header: t('…'), cell: (s) => <StatusPill kind="license" value={s.licenseState} />, value: (s) => s.licenseState, mobile: 'badge' },
 *       { id: 'accounts', header: t('…'), cell: …, align: 'end', mobile: 'meta' },
 *     ]}
 *     rowActions={(s) => [{ label: t('…'), onSelect: () => … }]}
 *     empty={<EmptyState … />}
 *   />
 * A filter's `id` is matched against the column with the same id (its `value`), or give `match`.
 *
 * Server mode: pass `manual`, `total`, `state` and `onStateChange` (e.g. from useUrlTableState()) and
 * feed `rows` from a query keyed on that state.
 */
import {
  ArrowDown,
  ArrowUp,
  ArrowsDownUp,
  CaretLeft,
  DotsThree,
  FunnelSimple,
  MagnifyingGlass,
  X,
} from '@phosphor-icons/react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { cn } from '@/lib/cn'
import { DropdownMenu, type MenuItem } from './DropdownMenu'
import { EmptyState } from './EmptyState'
import { ErrorState } from './ErrorState'
import { IconButton } from './IconButton'
import { Input } from './Input'
import { Pagination } from './Pagination'
import { Select } from './Select'
import { Skeleton } from './Skeleton'

export type Column<T> = {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  /** raw value for sorting, searching and filtering in client mode */
  value?: (row: T) => string | number | null | undefined
  sortable?: boolean
  align?: 'start' | 'end' | 'center'
  /** width or other classes for the column's cells */
  className?: string
  /** phones: 'title' heads the card, 'subtitle' under it, 'badge' beside it, 'meta' as label and value */
  mobile?: 'title' | 'subtitle' | 'badge' | 'meta' | 'hidden'
}

export type TableFilter<T> = {
  id: string
  label: string
  options: { value: string; label: string }[]
  /** client mode: custom match; default compares the column with the same id */
  match?: (row: T, value: string) => boolean
}

export type TableState = {
  search: string
  sort: { id: string; order: 'asc' | 'desc' } | null
  page: number
  pageSize: number
  filters: Record<string, string>
}

export const defaultTableState = (pageSize = 20): TableState => ({
  search: '',
  sort: null,
  page: 1,
  pageSize,
  filters: {},
})

/** Table state kept in the URL (`?q=&sort=name&order=asc&page=2&f_state=valid`), for shareable lists. */
export function useUrlTableState(pageSize = 20): [TableState, (s: TableState) => void] {
  const [params, setParams] = useSearchParams()
  const state: TableState = {
    search: params.get('q') ?? '',
    sort: params.get('sort')
      ? { id: params.get('sort')!, order: params.get('order') === 'desc' ? 'desc' : 'asc' }
      : null,
    page: Math.max(1, Number(params.get('page')) || 1),
    pageSize,
    filters: Object.fromEntries(
      [...params.entries()].filter(([k]) => k.startsWith('f_')).map(([k, v]) => [k.slice(2), v]),
    ),
  }
  const set = (s: TableState) => {
    const next = new URLSearchParams()
    for (const [k, v] of params.entries())
      if (!['q', 'sort', 'order', 'page'].includes(k) && !k.startsWith('f_')) next.set(k, v)
    if (s.search) next.set('q', s.search)
    if (s.sort) {
      next.set('sort', s.sort.id)
      next.set('order', s.sort.order)
    }
    if (s.page > 1) next.set('page', String(s.page))
    for (const [k, v] of Object.entries(s.filters)) if (v) next.set(`f_${k}`, v)
    setParams(next, { replace: true })
  }
  return [state, set]
}

type Props<T> = {
  caption: string
  rows: T[] | undefined
  columns: Column<T>[]
  getRowId: (row: T) => string
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  search?: { placeholder?: string }
  filters?: TableFilter<T>[]
  /** extra controls on the toolbar's end side */
  toolbar?: ReactNode
  rowActions?: (row: T) => MenuItem[]
  /** makes the row open a page: the title cell becomes a link and the row is clickable */
  rowHref?: (row: T) => string
  /** shown when there are no rows at all */
  empty?: ReactNode
  manual?: boolean
  total?: number
  state?: TableState
  onStateChange?: (s: TableState) => void
  pageSize?: number
  className?: string
  /** no outer panel (when already inside one) */
  bare?: boolean
}

export function DataTable<T>(props: Props<T>) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const {
    caption,
    rows,
    columns,
    getRowId,
    loading,
    error,
    onRetry,
    search,
    filters,
    toolbar,
    rowActions,
    rowHref,
    empty,
    manual,
  } = props
  const [internal, setInternal] = useState<TableState>(() => defaultTableState(props.pageSize))
  const state = props.state ?? internal
  const setState = (s: TableState) => (props.onStateChange ? props.onStateChange(s) : setInternal(s))
  const patch = (p: Partial<TableState>) => setState({ ...state, page: 1, ...p })

  const processed = useMemo(() => {
    if (!rows) return { page: [] as T[], total: 0 }
    if (manual) return { page: rows, total: props.total ?? rows.length }
    let list = rows
    const q = state.search.trim().toLowerCase()
    if (q) {
      const searchable = columns.filter((c) => c.value)
      list = list.filter((r) =>
        searchable.some((c) =>
          String(c.value!(r) ?? '')
            .toLowerCase()
            .includes(q),
        ),
      )
    }
    for (const [id, v] of Object.entries(state.filters)) {
      if (!v) continue
      const f = filters?.find((x) => x.id === id)
      const col = columns.find((c) => c.id === id)
      list = list.filter((r) => (f?.match ? f.match(r, v) : String(col?.value?.(r) ?? '') === v))
    }
    if (state.sort) {
      const col = columns.find((c) => c.id === state.sort!.id)
      if (col?.value) {
        const dir = state.sort.order === 'desc' ? -1 : 1
        list = [...list].sort((a, b) => {
          const av = col.value!(a)
          const bv = col.value!(b)
          if (av == null) return 1
          if (bv == null) return -1
          return (
            (typeof av === 'number' && typeof bv === 'number'
              ? av - bv
              : String(av).localeCompare(String(bv))) * dir
          )
        })
      }
    }
    const start = (state.page - 1) * state.pageSize
    return { page: list.slice(start, start + state.pageSize), total: list.length }
  }, [rows, manual, props.total, state, columns, filters])

  const hasQuery = Boolean(state.search) || Object.values(state.filters).some(Boolean)
  const isEmpty = !loading && !error && rows && rows.length === 0 && !hasQuery
  const noResults = !loading && !error && rows && processed.total === 0 && hasQuery

  const toggleSort = (id: string) => {
    const cur = state.sort
    const next =
      !cur || cur.id !== id
        ? { id, order: 'asc' as const }
        : cur.order === 'asc'
          ? { id, order: 'desc' as const }
          : null
    setState({ ...state, sort: next })
  }

  const titleCol = columns.find((c) => c.mobile === 'title') ?? columns[0]!
  const subtitleCol = columns.find((c) => c.mobile === 'subtitle')
  const badgeCol = columns.find((c) => c.mobile === 'badge')
  const metaCols = columns.filter((c) => c.mobile === 'meta' || (!c.mobile && c !== titleCol))

  const showToolbar = search || filters?.length || toolbar
  const alignCls = (a?: Column<T>['align']) =>
    a === 'end' ? 'text-end' : a === 'center' ? 'text-center' : 'text-start'

  const body = (
    <>
      {showToolbar ? (
        <div className="flex flex-col gap-2 border-b border-hairline p-3 sm:flex-row sm:items-center sm:p-4">
          {search ? (
            <Input
              type="search"
              value={state.search}
              onChange={(e) => patch({ search: e.target.value })}
              placeholder={search.placeholder ?? t('ui.table.search')}
              aria-label={search.placeholder ?? t('ui.table.search')}
              start={<MagnifyingGlass />}
              className="sm:max-w-xs"
              end={
                state.search ? (
                  <button
                    type="button"
                    onClick={() => patch({ search: '' })}
                    aria-label={t('actions.clear')}
                    className="grid size-7 place-items-center rounded-md text-subtle hover:bg-control hover:text-ink"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                ) : undefined
              }
            />
          ) : null}
          {filters?.map((f) => (
            <Select
              key={f.id}
              aria-label={f.label}
              value={state.filters[f.id] ?? '__all'}
              onValueChange={(v) => patch({ filters: { ...state.filters, [f.id]: v === '__all' ? '' : v } })}
              start={<FunnelSimple />}
              className="sm:w-48"
              options={[{ value: '__all', label: `${f.label}: ${t('ui.table.all')}` }, ...f.options]}
            />
          ))}
          {hasQuery ? (
            <button
              type="button"
              onClick={() => patch({ search: '', filters: {} })}
              className="h-9 self-start rounded-lg px-2 text-13 font-medium text-brand-700 hover:bg-brand-50 sm:self-auto"
            >
              {t('ui.table.clearFilters')}
            </button>
          ) : null}
          {toolbar ? <div className="flex items-center gap-2 sm:ms-auto">{toolbar}</div> : null}
        </div>
      ) : null}

      {error ? (
        <ErrorState error={error} onRetry={onRetry} compact />
      ) : isEmpty ? (
        (empty ?? <EmptyState compact title={t('ui.table.empty')} />)
      ) : noResults ? (
        <EmptyState
          compact
          icon={<MagnifyingGlass />}
          title={t('ui.table.noResults')}
          description={t('ui.table.noResultsHint')}
        />
      ) : (
        <>
          {/* md and up: a real table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse text-14">
              <caption className="sr-only">{caption}</caption>
              <thead>
                <tr className="border-b border-hairline">
                  {columns.map((c) => {
                    const sorted = state.sort?.id === c.id ? state.sort.order : null
                    return (
                      <th
                        key={c.id}
                        scope="col"
                        aria-sort={
                          sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined
                        }
                        className={cn(
                          'h-10 px-4 text-12 font-semibold whitespace-nowrap text-subtle first:ps-5 last:pe-5',
                          alignCls(c.align),
                          c.className,
                        )}
                      >
                        {c.sortable ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(c.id)}
                            className={cn(
                              '-mx-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-control hover:text-ink',
                              sorted && 'text-ink',
                            )}
                          >
                            {c.header}
                            {sorted === 'asc' ? (
                              <ArrowUp className="size-3" weight="bold" aria-hidden="true" />
                            ) : sorted === 'desc' ? (
                              <ArrowDown className="size-3" weight="bold" aria-hidden="true" />
                            ) : (
                              <ArrowsDownUp className="size-3 opacity-50" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          c.header
                        )}
                      </th>
                    )
                  })}
                  {rowActions ? (
                    <th scope="col" className="w-14 pe-3">
                      <span className="sr-only">{t('ui.table.actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {loading && !rows?.length
                  ? Array.from({ length: 5 }, (_, i) => (
                      <tr key={i} className="border-b border-hairline last:border-0">
                        {columns.map((c, j) => (
                          <td key={c.id} className="h-14 px-4 first:ps-5 last:pe-5">
                            <Skeleton className={cn('h-3.5', j === 0 ? 'w-40' : 'w-20')} />
                          </td>
                        ))}
                        {rowActions ? <td /> : null}
                      </tr>
                    ))
                  : processed.page.map((row) => {
                      const href = rowHref?.(row)
                      return (
                        <tr
                          key={getRowId(row)}
                          onClick={
                            href
                              ? (e) =>
                                  !(e.target as HTMLElement).closest('a,button,[role=menuitem]') &&
                                  navigate(href)
                              : undefined
                          }
                          className={cn(
                            'group border-b border-hairline transition-colors last:border-0',
                            href && 'cursor-pointer hover:bg-surface-soft',
                          )}
                        >
                          {columns.map((c) => (
                            <td
                              key={c.id}
                              className={cn(
                                'h-14 px-4 align-middle first:ps-5 last:pe-5',
                                alignCls(c.align),
                                c.className,
                              )}
                            >
                              {href && c === titleCol ? (
                                <Link
                                  to={href}
                                  className="font-medium text-ink outline-offset-2 hover:text-brand-700"
                                >
                                  {c.cell(row)}
                                </Link>
                              ) : (
                                c.cell(row)
                              )}
                            </td>
                          ))}
                          {rowActions ? (
                            <td className="pe-3 text-end">
                              <RowMenu items={rowActions(row)} />
                            </td>
                          ) : null}
                        </tr>
                      )
                    })}
              </tbody>
            </table>
          </div>

          {/* phones: stacked cards */}
          <ul className="flex flex-col md:hidden" aria-label={caption}>
            {loading && !rows?.length
              ? Array.from({ length: 4 }, (_, i) => (
                  <li key={i} className="flex flex-col gap-2 border-b border-hairline p-4 last:border-0">
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-3 w-1/3" />
                  </li>
                ))
              : processed.page.map((row) => {
                  const href = rowHref?.(row)
                  return (
                    <li
                      key={getRowId(row)}
                      className="relative flex flex-col gap-3 border-b border-hairline p-4 last:border-0"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          {href ? (
                            <Link
                              to={href}
                              className="font-semibold text-ink after:absolute after:inset-0 after:content-['']"
                            >
                              {titleCol.cell(row)}
                            </Link>
                          ) : (
                            <span className="font-semibold text-ink">{titleCol.cell(row)}</span>
                          )}
                          {subtitleCol ? (
                            <span className="text-13 text-muted">{subtitleCol.cell(row)}</span>
                          ) : null}
                        </div>
                        {badgeCol ? <div className="shrink-0">{badgeCol.cell(row)}</div> : null}
                        {rowActions ? (
                          <div className="relative z-10 -me-2 -mt-1">
                            <RowMenu items={rowActions(row)} />
                          </div>
                        ) : href ? (
                          <CaretLeft
                            className="mt-1 size-4 shrink-0 flip-ltr text-subtle"
                            aria-hidden="true"
                          />
                        ) : null}
                      </div>
                      {metaCols.filter((c) => c !== subtitleCol && c !== badgeCol && c.mobile !== 'hidden')
                        .length ? (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                          {metaCols
                            .filter((c) => c !== subtitleCol && c !== badgeCol && c.mobile !== 'hidden')
                            .map((c) => (
                              <div key={c.id} className="flex min-w-0 flex-col gap-0.5">
                                <dt className="text-12 text-subtle">{c.header}</dt>
                                <dd className="min-w-0 truncate text-13 text-ink">{c.cell(row)}</dd>
                              </div>
                            ))}
                        </dl>
                      ) : null}
                    </li>
                  )
                })}
          </ul>

          {processed.total > state.pageSize || state.page > 1 ? (
            <Pagination
              page={state.page}
              pageSize={state.pageSize}
              total={processed.total}
              onPageChange={(page) => setState({ ...state, page })}
              className="border-t border-hairline px-4 py-3 sm:px-5"
            />
          ) : null}
        </>
      )}
    </>
  )

  if (props.bare) return <div className={props.className}>{body}</div>
  return (
    <div
      className={cn(
        'overflow-hidden rounded-panel bg-surface shadow-panel ring-1 ring-card-line',
        props.className,
      )}
    >
      {body}
    </div>
  )
}

function RowMenu({ items }: { items: MenuItem[] }) {
  const { t } = useTranslation('common')
  if (!items.length) return null
  return (
    <DropdownMenu
      trigger={
        <IconButton
          label={t('ui.table.actions')}
          icon={<DotsThree weight="bold" />}
          size="sm"
          tooltip={false}
        />
      }
      items={items}
    />
  )
}
